import { add } from 'date-fns'
import {
  RoomSearchParamSchema,
  RoomSearchParam,
  HotelResultsFormState,
} from '@reward-platform/ancillaries-schemas/hotel'
import { AncillariesLocation } from '@reward-platform/ancillaries-locations'
import { preserveDateWhenConvertedToUTC } from '~/utils/datetime'

const DEFAULT_ROOMS: Array<RoomSearchParam> = [
  {
    adults: 2,
    children: 0,
    roomNumber: 1,
  },
]

export const DEFAULT_DESTINATION: AncillariesLocation = {
  id: '',
  type: '',
  name: '',
  fullName: '',
  countryCode: 'GB',
}

export const getDefaultHotelResultsFormState = (): HotelResultsFormState => ({
  destination: DEFAULT_DESTINATION,
  rooms: DEFAULT_ROOMS,
  startDateTime: preserveDateWhenConvertedToUTC(add(new Date(), { hours: 28 })),
  endDateTime: preserveDateWhenConvertedToUTC(add(new Date(), { hours: 52 })),
})

const validateAge = (ageStr: string): number | null => {
  const age = Number.parseInt(ageStr, 10)
  if (!Number.isNaN(age) && age >= 0 && age <= 17) {
    return age
  }
  return null
}

/** parses comma separated list in the format <adult_number>|<child_age>;<child_age> is equivalent to
 * { adults: <adult_number>, children: <child_age.count>, childrenAges: [{ child: 'child_1', age: <child_age[0]> }, { child: 'child_2', age: <child_age[1]> }] }
 * example: 2|6,2|4;14,2|3|4;5 is equivalent to
 * [
 *   { adults: 2, children: 1, childrenAges: [{ child: 'child_1', age: 6 }], roomNumber: 1 },
 *   { adults: 2, children: 2, childrenAges: [{ child: 'child_1', age: 4 }, { child: 'child_2', age: 14 }], roomNumber: 2 },
 *   { adults: 2, children: 3, childrenAges: [{ child: 'child_1', age: 4 }, { child: 'child_2', age: 5 }], roomNumber: 3 },
 * ]
 */
export const parseRoomsFromQueryString = (roomsParam: string): Array<RoomSearchParam> => {
  const roomStrings = roomsParam.split(',')

  const rooms: Array<RoomSearchParam> = roomStrings.reduce<Array<RoomSearchParam>>(
    (acc, roomString, roomIndex) => {
      const [adultsStr, childrenAgesStr] = roomString.split('|')
      const adults = Number.parseInt(adultsStr, 10) || 2
      const childrenAgesList = childrenAgesStr
        ?.split(';')
        .filter((ageStr) => validateAge(ageStr) !== null)
        .map((childAgeStr, childIndex) => {
          const age = Number.parseInt(childAgeStr, 10)
          return { child: `child_${childIndex + 1}`, age }
        })
      const children = childrenAgesList?.length || 0
      const childrenAges = childrenAgesList?.length > 0 ? childrenAgesList : undefined

      const maybeRoom = { adults, children, childrenAges, roomNumber: roomIndex + 1 }
      const parse = RoomSearchParamSchema.safeParse(maybeRoom)
      if (parse.success) {
        acc.push(parse.data)
      }
      return acc
    },
    []
  )

  return rooms.length > 0 ? rooms : DEFAULT_ROOMS
}

export const stringifyRoomsFromData = (roomsParam: Array<RoomSearchParam>): string => {
  const roomStrings = roomsParam.map((room) => {
    const children = room.childrenAges?.map((childAge) => childAge.age).join(';') || ''

    return [String(room.adults), children].filter((part) => part !== '').join('|')
  })
  return roomStrings.join(',')
}
